import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Boxes, ShieldCheck, Users, FileText, LogIn } from 'lucide-react'
import { login, getValidAccessToken } from '../auth'
import hero from '../assets/hero.png'

export default function Login() {
  const navigate = useNavigate()

  // Already signed in? Go straight to the dashboard.
  useEffect(() => {
    getValidAccessToken().then((token) => {
      if (token) navigate('/', { replace: true })
    })
  }, [navigate])

  return (
    <div className="flex min-h-screen bg-white">
      {/* Brand panel */}
      <div className="relative hidden w-[55%] overflow-hidden lg:block">
        <img src={hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-950/90 via-brand-900/70 to-brand-900/80" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
              <Boxes size={22} />
            </div>
            <div>
              <p className="font-bold tracking-tight">EDP Portal</p>
              <p className="text-xs text-brand-200">Enterprise Digitalized Platform</p>
            </div>
          </div>

          <div className="max-w-md">
            <h2 className="text-4xl font-extrabold leading-tight tracking-tight">
              One workspace for your whole organization.
            </h2>
            <p className="mt-4 text-brand-100/90">
              Manage people, documents, and org structure from a single,
              secure employee self-service portal.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                { icon: Users, text: 'Employee directory & profiles' },
                { icon: FileText, text: 'Centralized document management' },
                { icon: ShieldCheck, text: 'Enterprise single sign-on (SSO)' },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3 text-brand-100">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15">
                    <Icon size={15} />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-brand-200/70">
            © 2026 Enterprise Digitalized Platform · Internal use only
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center bg-slate-50 px-4 py-12 sm:px-8">
        <div className="w-full max-w-md animate-fade-up">
          {/* Mobile brand */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-500/25">
              <Boxes size={20} />
            </div>
            <p className="font-bold tracking-tight">EDP Portal</p>
          </div>

          <h1 className="text-2xl font-extrabold tracking-tight">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-500">
            Sign in with your corporate account to continue.
          </p>

          <button
            onClick={() => void login()}
            className="group mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:shadow-brand-500/30 hover:brightness-110"
          >
            <LogIn size={17} />
            Sign in with SSO
          </button>

          <div className="mt-6 flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
            <ShieldCheck size={16} className="shrink-0 text-brand-500" />
            Secured by Keycloak single sign-on. Your credentials never leave
            your identity provider.
          </div>

          <p className="mt-8 text-center text-xs text-slate-400">
            Having trouble? Contact IT support to access your account.
          </p>
        </div>
      </div>
    </div>
  )
}
